import React from 'react';

import { PlatformCaretDownIcon } from './PlatformIcons';
import { PLATFORM_SKINS } from './platform-tokens';
import { useResolvedDarkMode } from './useResolvedDarkMode';

import { cn } from '@/lib/utils';

/**
 * Google RESPONSIVE SEARCH AD replica.
 *
 * ── THE CAVEAT THAT SHAPES THE WHOLE COMPONENT ────────────────────────────
 * An RSA has no single appearance. Google assembles a different combination
 * of the supplied headlines and descriptions for every auction, so any
 * picture of "the ad" is one sample from a very large set. Showing one
 * combination as though it were the finished ad would teach the writer to
 * polish an arrangement the reader will probably never see.
 *
 * So the replica renders a NAMED combination — the first three headlines and
 * first two descriptions, the shape Google most commonly serves — and says
 * out loud that it is one of many. `combinationIndex` lets a caller step
 * through alternatives so the writer can check that the assets really do
 * read correctly in more than one order, which is the actual craft
 * requirement.
 *
 * ── PROVENANCE ────────────────────────────────────────────────────────────
 * Structure and skin come from Figma `Dl0gE7lH27N7x7OJb0DFBw`, frames
 * `28:13041` (light) and `28:13042` (dark), read 2026-08-21. The frame is a
 * 393px-wide mobile SERP card: 16px padding, a bold `Ad` label and the
 * display URL on one row with a caret after it, the headline beneath in the
 * link hue, and the description beneath that in the secondary hue.
 *
 * Colours come from `PLATFORM_SKINS['google-search']`, whose values were
 * already ported from those same two frames into `--platform-google-search-*`
 * in `src/index.css`. This file previously predated the skin system and
 * painted itself in MORA's semantic tokens (`bg-card`, `text-primary`,
 * `text-muted-foreground`) — which made it the one ad replica that changed
 * appearance with Mora's brand rather than with Google's, the exact failure
 * the program's §2 ruling exists to prevent: a preview looks like the
 * PLATFORM, not like Mora.
 *
 * ── WHAT IS DELIBERATELY *NOT* FROM FIGMA ─────────────────────────────────
 * The Figma sample shows a single placeholder headline. A real RSA serves up
 * to three, pipe-separated, and the point of this surface is to review an
 * inventory of assets — so the joined-headlines behaviour is kept. Structure
 * and skin from Figma; the ad's real serving behaviour from Google's
 * published RSA spec.
 */
export interface GoogleRsaAdPreviewProps {
  /** Display domain, e.g. "javacityroasters.com". */
  destinationDomain?: string | null;
  /** Display paths appended to the domain. */
  paths?: string[];
  headlines: string[];
  descriptions: string[];
  /** Which sample combination to show. Rotates through the supplied assets. */
  combinationIndex?: number;
  /**
   * Forces which Google skin variant renders, independent of Mora's active
   * theme. Omit to follow Mora's theme (the normal, real-app path); pass
   * explicitly for the style guide's side-by-side light/dark comparison and
   * for tests that must render a deterministic variant. Same contract as
   * `MetaAdPreview`/`InstagramAdPreview`/`LinkedInAdPreview`.
   */
  theme?: 'light' | 'dark';
  className?: string;
}

const RSA_HEADLINES_SHOWN = 3;
const RSA_DESCRIPTIONS_SHOWN = 2;

/**
 * The Figma frame's own width (`28:13041` is 393px, a mobile SERP). Not
 * `platformNaturalMaxWidth()` — that switch is keyed on
 * `PlatformPreviewKind`, and an ad unit never carries one (see the paid
 * replicas' file headers for the same deliberate omission).
 */
const GOOGLE_SEARCH_AD_MAX_WIDTH = 393;

function rotate<T>(items: T[], offset: number, count: number): T[] {
  if (items.length === 0) return [];
  return Array.from({ length: Math.min(count, items.length) }, (_, index) => {
    const position = (offset + index) % items.length;
    return items[position];
  });
}

export const GoogleRsaAdPreview: React.FC<GoogleRsaAdPreviewProps> = ({
  destinationDomain,
  paths,
  headlines,
  descriptions,
  combinationIndex = 0,
  theme,
  className,
}) => {
  const isDarkMode = useResolvedDarkMode();
  const resolvedTheme = theme ?? (isDarkMode ? 'dark' : 'light');
  const skin = PLATFORM_SKINS['google-search'][resolvedTheme];

  const domain = destinationDomain?.trim();
  const shownHeadlines = rotate(headlines, combinationIndex, RSA_HEADLINES_SHOWN);
  const shownDescriptions = rotate(descriptions, combinationIndex, RSA_DESCRIPTIONS_SHOWN);
  const displayPath = [domain, ...(paths ?? []).filter(Boolean)].join(' › ');

  return (
    <div
      className={cn('mx-auto w-full min-w-0', className)}
      style={{ maxWidth: `${GOOGLE_SEARCH_AD_MAX_WIDTH}px` }}
    >
      {/*
        🔴 LIGHT KEEPS THE BORDER, DARK DOES NOT — and the asymmetry is the
        point, so do not "tidy" it into one rule.

        Unlike Meta's and Instagram's frames, which carry no stroke on their
        root at all, BOTH Google frames draw a 1px border, and both draw it in
        the same colour: sampling the rendered frames pixel by pixel returns
        `rgb(194,197,200)` — `#c2c5c8` — hard against the card edge in the
        light frame AND in the dark one.

        In light that is plausible and close to Google's own `#dadce0`. In dark
        it is not: a 76%-luminance hairline on a 19%-luminance card is a ~4:1
        stroke, and no dark UI draws a divider that loud. Google's dark SERP
        card has no visible outline. What the dark frame almost certainly has
        is the light frame's border swatch, copy-pasted — the Figma is the
        source of truth for STRUCTURE and SKIN, but when it contradicts the
        platform it depicts, the platform wins, because rule 1 of this program
        is that a preview looks like the PLATFORM. A preview faithful to a
        mistaken mock is still a lie about Google.

        Founder, 2026-08-21, unprompted and on sight: _"we didn't fix the
        Google Ads shadows and borders on that component… if you click the
        preview in Google Ads, you'll see that one looks incorrect."_ He is
        looking at this stroke.

        This DROPS an unsupportable value rather than inventing a replacement —
        the same move `FacebookPost` made with `#B0B3B8`. If a sourced Google
        dark outline ever turns up, add it here with the citation.
      */}
      <article
        className={cn(
          'w-full min-w-0 overflow-hidden rounded-lg p-4',
          resolvedTheme === 'light' && 'border',
        )}
        data-platform="google-rsa-ad"
        style={{
          backgroundColor: skin.cardBackground,
          ...(resolvedTheme === 'light' ? { borderColor: skin.cardBorder } : {}),
        }}
      >
        <div className="flex items-center gap-1.5">
          <span className="text-detail font-semibold" style={{ color: skin.primaryText }}>
            Ad
          </span>
          <span aria-hidden="true" style={{ color: skin.primaryText }}>
            •
          </span>
          {/*
            No destination URL is the NORMAL state of a Mora ad unit —
            `ad_units` has no destination column — so the slot says so instead
            of printing a plausible-looking domain nobody entered. Same
            honesty contract as `MetaAdPreview`'s `destinationDomain={null}`.
          */}
          <span
            className={cn(
              'min-w-0 truncate',
              domain ? 'text-detail' : 'text-[13px] italic leading-5',
            )}
            style={{ color: domain ? skin.primaryText : skin.secondaryText }}
          >
            {domain ? displayPath : 'No destination URL yet'}
          </span>
          {/* The caret FOLLOWS the URL rather than pinning to the card's
              right edge — in the Figma frame it sits at x=187 directly
              after a 165px-wide URL, not at the 361px container edge.
              Pinning it right turns Google's inline affordance into a
              toolbar. */}
          <PlatformCaretDownIcon
            className="shrink-0"
            size={8}
            style={{ color: skin.primaryText }}
          />
        </div>

        <p
          className="mt-1.5 break-words text-sm font-normal leading-snug"
          style={{ color: skin.linkText ?? skin.primaryText }}
        >
          {shownHeadlines.length > 0 ? shownHeadlines.join(' | ') : 'No headlines yet'}
        </p>

        <p
          className="mt-1 break-words text-detail leading-relaxed"
          style={{ color: skin.secondaryText }}
        >
          {shownDescriptions.length > 0 ? shownDescriptions.join(' ') : 'No descriptions yet'}
        </p>
      </article>

      {/*
        The disclaimer sits OUTSIDE the card on purpose: it is Mora talking
        about the ad, not part of the ad. Anything inside the article is a
        claim about what Google renders.
      */}
      <p className="mt-2 text-center text-detail text-muted-foreground">
        One of many combinations. Google mixes these {headlines.length}{' '}
        {headlines.length === 1 ? 'headline' : 'headlines'} and {descriptions.length}{' '}
        {descriptions.length === 1 ? 'description' : 'descriptions'} differently for every search.
      </p>
    </div>
  );
};
